import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { useAlertsService } from '../services';
import { AlertRequestData, GetAlertsParams } from '../types';

export const useAlertsViewController = () => {
  const {
    loading: alertsLoading,
    filter: alertsFilter,
    list: alertsList,
    getAlerts,
    loadMoreAlerts,
    changeAlertsFilter,
    resetAlerts,
    createAlert,
    updateAlert,
    deleteAlert,
  } = useAlertsService();

  const initLoad = useCallback(async () => {
    try {
      await getAlerts();
    } catch (e) {
      console.log(e);
    }
  }, [getAlerts]);

  const reloadAlerts = useCallback(
    async (isSearch = false) => {
      try {
        await getAlerts(false, isSearch);
      } catch (e) {
        console.log(e);
      }
    },
    [getAlerts],
  );

  const reloadAlertsDebounce = useMemo(() => debounce(reloadAlerts, 500), [reloadAlerts]);

  const changeAlertFilterForm = useCallback(
    async (filter: Partial<RootState['alerts']['filter']>) => {
      changeAlertsFilter(filter);
      await reloadAlerts();
    },
    [changeAlertsFilter, reloadAlerts],
  );

  const changeAlertsSearchFilter = useCallback(
    async (_: ChangeEvent<HTMLInputElement>, query: string) => {
      changeAlertsFilter({ query: query.trim() || undefined });
      await reloadAlertsDebounce(true);
    },
    [changeAlertsFilter, reloadAlertsDebounce],
  );

  const changeAlertsSorting = useCallback(
    async (s: GridSortModel) => {
      const sortValue = s.length > 0 ? `${s[0].field}_${s[0].sort}` : 'company_name_asc';
      changeAlertsFilter({
        sort: sortValue as GetAlertsParams['sort'],
      });
      await reloadAlerts();
    },
    [changeAlertsFilter, reloadAlerts],
  );

  const changeAlertsPagination = useCallback(
    async (p: GridPaginationModel) => {
      changeAlertsFilter({ limit: p.pageSize, offset: p.page * p.pageSize });
      await reloadAlerts();
    },
    [changeAlertsFilter, reloadAlerts],
  );

  const createAlertData = useCallback(
    async (alertData: AlertRequestData) => {
      try {
        const result = await createAlert(alertData);
        return result;
      } catch (e) {
        console.log(e);
        throw e;
      }
    },
    [createAlert],
  );

  const updateAlertData = useCallback(
    async (alertId: string, alertData: AlertRequestData) => {
      try {
        const result = await updateAlert(alertId, alertData);
        return result;
      } catch (e) {
        console.log(e);
        throw e;
      }
    },
    [updateAlert],
  );

  const deleteAlertData = useCallback(
    async (alertId: string) => {
      try {
        await deleteAlert(alertId);
      } catch (e) {
        console.log(e);
        throw e;
      }
    },
    [deleteAlert],
  );

  useEffect(() => {
    initLoad();

    return () => {
      resetAlerts();
    };
  }, [initLoad, resetAlerts]);

  return {
    isPageLoading: alertsLoading.getList,
    isSearching: alertsLoading.search,
    isCreating: alertsLoading.create,
    isUpdating: alertsLoading.update,
    isDeleting: alertsLoading.delete,
    alertsFilter,
    alertsList,
    loadMoreAlerts,
    changeAlertFilterForm,
    changeAlertsSearchFilter,
    changeAlertsSorting,
    changeAlertsPagination,
    createAlertData,
    updateAlertData,
    deleteAlertData,
  };
};